import { useEffect, useMemo, useState } from "react";
import { useScene } from "./use-scene";

export interface ElementTimerState {
  /** Whether the countdown is currently running. */
  active: boolean;
  /** Seconds remaining until the block auto-continues. */
  remaining: number;
  /** The configured duration in seconds. */
  total: number;
}

/**
 * Central countdown timer for content blocks. When a scene has a configured
 * `timerDuration` and the block is the active (focused) slide, the timer counts
 * down and, on reaching zero, triggers `scene.lifecycle.timeout()` once — which
 * applies the maximal wrong-answer penalty (leaving the lowest possible points),
 * answers the block, and continues. The finish is briefly delayed so the
 * answered state is visible, unless a points/explanation screen follows.
 *
 * The timer only runs while the block is unanswered and no points/explanation
 * screen is showing, mirroring the guard used by `useDelayedAnswer`.
 *
 * When `paused` is true the timer is still displayed (at its full duration) but
 * never counts down or auto-continues — used for the builder preview, so the
 * author sees the timer without it running.
 *
 * @param active whether the block is the focused/active slide
 * @param paused when true, show the timer but never count down / auto-continue
 */
export const useElementTimer = (
  active: boolean,
  paused = false
): ElementTimerState => {
  const scene = useScene();
  const total = scene.timerDuration;

  const running = useMemo(() => {
    if (!active || !total) return false;
    if (scene.isFinished || scene.isAnswered) return false;
    return !scene.showingPoints && !scene.showingExplanation;
  }, [
    active,
    total,
    scene.isFinished,
    scene.isAnswered,
    scene.showingPoints,
    scene.showingExplanation,
  ]);

  const [remaining, setRemaining] = useState(total);

  // A single effect owns the countdown: it (re)starts whenever the timer
  // becomes active, ticks down a local counter every second and, on reaching
  // zero, continues the block exactly once. Using a local counter (rather than
  // the `remaining` state) avoids any stale-state race on restart.
  useEffect(() => {
    if (!running) return undefined;

    // Always reset to the full duration when (re)activating.
    let current = total;
    setRemaining(current);

    // Paused (e.g. builder preview): render the timer but do not run it.
    if (paused) return undefined;

    const interval = setInterval(() => {
      current = Math.max(current - 1, 0);
      setRemaining(current);

      if (current <= 0) {
        clearInterval(interval);
        scene.lifecycle.timeout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [running, paused, total, scene]);

  return { active: running, remaining, total };
};
